import { AutoAwesome } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserCustomization } from '~/backend/types';
import AvailableModels from '~/components/ModelSelection/AvailableModels';
import CustomizationSelection from '~/components/ModelSelection/CustomizationSelection';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import NumberInput from '~/components/Inputs/NumberInput';
import useModalityCompatibleModels from '~/components/ModelSelection/useModalityCompatibleModels';
import { Modality } from '~/domain/dicom/modality';
import { usePrimaryImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import {
    ISRO_DEFAULT_SAMPLES,
    ISRO_MAX_SAMPLES,
    ISRO_MIN_SAMPLES,
    isIsroModel,
    isroDeploymentEnabled,
} from '~/hooks/workflows/isro/isroSamples';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type ImageTranslationModelSelectionProps = {
    modelCustomizations?: UserCustomization<unknown>[];
    availableModels?: PredictionModel[];
    modality?: Modality;
    disabled?: boolean;
    isLoadingModels?: boolean;
    isSubmitting?: boolean;
    showPseudonymization: boolean;
    supportsSampleCount: boolean;
    onSubmit: (
        model: PredictionModel,
        selectedCustomization?: string,
        numSamples?: number
    ) => void;
};

export default function ImageTranslationModelSelection({
    availableModels = [],
    modelCustomizations = [],
    modality,
    disabled = false,
    isLoadingModels,
    isSubmitting,
    showPseudonymization,
    supportsSampleCount,
    onSubmit,
}: ImageTranslationModelSelectionProps) {
    // The task ships `getSlicesAsDicomBlobs()`; on an incomplete image that
    // yields only the slices that arrived, and the model answers for a volume
    // that is not the patient's without anything looking wrong.
    const imageGate = usePrimaryImageCompletenessGate();
    const [selectedModel, setSelectedModel] = useState<PredictionModel>();
    const [selectedCustomization, setSelectedCustomization] =
        useState<string>();
    const [numSamples, setNumSamples] = useState<number>(ISRO_DEFAULT_SAMPLES);
    const [isNumSamplesValid, setIsNumSamplesValid] = useState(true);
    const isroEnabled = isroDeploymentEnabled();
    const showSampleCount =
        isroEnabled && supportsSampleCount && isIsroModel(selectedModel);
    const offeredModels = useMemo(
        () =>
            isroEnabled
                ? availableModels
                : availableModels.filter((model) => !isIsroModel(model)),
        [availableModels, isroEnabled]
    );

    const {
        compatibleModels,
        emptyMessage,
        labelAdornment,
        isSelectedModelCompatible,
    } = useModalityCompatibleModels(
        offeredModels,
        modality,
        selectedModel,
        setSelectedModel
    );

    const customizations = useMemo(() => {
        if (!selectedModel || !modelCustomizations) {
            return [];
        }
        const selectedModelCustomizations =
            selectedModel?.customizations?.filter(
                (customization) => customization.name !== selectedModel.name
            );
        return selectedModelCustomizations || [];
    }, [selectedModel, modelCustomizations]);

    const hasCustomizations = customizations.length > 0;

    return (
        <>
            <Box
                display="grid"
                gridTemplateRows="240px auto"
                gridTemplateColumns={hasCustomizations ? '60% 1fr' : '100%'}
                gap={hasCustomizations ? 1 : 0}
            >
                <AvailableModels
                    isLoading={isLoadingModels}
                    models={compatibleModels}
                    selectedModel={selectedModel}
                    onSelectedModelChanged={setSelectedModel}
                    emptyMessage={emptyMessage}
                    labelAdornment={labelAdornment}
                />

                {hasCustomizations && (
                    <CustomizationSelection
                        customizations={customizations}
                        selectedCustomization={selectedCustomization}
                        onSelectedCustomizationChanged={
                            setSelectedCustomization
                        }
                    />
                )}
            </Box>
            {showSampleCount && (
                <Box mt={1} mb={0.5} width={160}>
                    <NumberInput
                        label={<FormattedMessage id="isro.numberOfSamples" />}
                        value={numSamples}
                        onChange={(value) =>
                            value !== null && setNumSamples(value)
                        }
                        onValidityChange={setIsNumSamplesValid}
                        min={ISRO_MIN_SAMPLES}
                        max={ISRO_MAX_SAMPLES}
                        step={1}
                        precision={0}
                        fullWidth
                        data-testid="isro-num-samples"
                    />
                </Box>
            )}
            <Box display="flex" justifyContent="flex-end">
                <Stack gap={0.5}>
                    {showPseudonymization && <PseudonymizeCheckbox />}
                    <Tooltip
                        title={
                            imageGate.isBlocked ? (
                                imageGate.reason
                            ) : !selectedModel ? (
                                <FormattedMessage id="selectAModelToStart" />
                            ) : (
                                ''
                            )
                        }
                        placement="left-start"
                    >
                        {/* Wrapper needed for MUI Tooltip to work on disabled buttons */}
                        <Box component="span">
                            <Button
                                onClick={() =>
                                    selectedModel &&
                                    onSubmit(
                                        selectedModel,
                                        selectedCustomization,
                                        showSampleCount ? numSamples : undefined
                                    )
                                }
                                variant="contained"
                                endIcon={<AutoAwesome />}
                                disabled={
                                    !selectedModel ||
                                    disabled ||
                                    isSubmitting ||
                                    !isSelectedModelCompatible ||
                                    imageGate.isBlocked ||
                                    (showSampleCount && !isNumSamplesValid)
                                }
                                sx={{ width: 160 }}
                            >
                                <FormattedMessage id="start" />
                            </Button>
                        </Box>
                    </Tooltip>
                </Stack>
            </Box>
        </>
    );
}
